import type { LucideIcon } from "lucide-react";

import {
  BarChart3,
  Database,
  FileText,
  Home,
  Package,
  Send,
  Settings,
  Sliders,
  Users,
} from "lucide-react";

import { PATHS } from "@/app/routes/paths";
import type { PermissionCode } from "@/core/authz/permissions";

export interface NavItem {
  /** Menu label shown in the sidebar. */
  label: string;
  /** Page title shown in the header. */
  title: string;
  path: string;
  icon: LucideIcon;
  /**
   * Grant that reveals this entry. Always the module's `view` code: the
   * sidebar answers "can this operator open the screen at all", and the
   * finer create/update/delete grants gate the controls inside it.
   * Omitted only for entries every signed-in user may reach.
   */
  permission?: PermissionCode;
  /**
   * Stay highlighted for every path beneath `path`. Used by screens that
   * carry their own tabs (Master Management), where the sub-page lives in
   * the URL but the sidebar should keep showing one selected entry.
   */
  matchNested?: boolean;
}

export interface NavSection {
  id: string;
  label: string;
  /**
   * Grant that reveals the whole group. A section is also dropped when
   * every one of its items is hidden, so this is only needed where the
   * header itself names a module (`comingSoon` / `locked` groups, which
   * carry no items to filter).
   */
  permission?: PermissionCode;
  /** Sections without items render as disabled "coming soon" entries. */
  items: NavItem[];
  /**
   * A section that is itself one destination: rendered as a single link
   * rather than an expandable group, so a module with many sub-screens does
   * not have to spend a long list of sidebar rows on them.
   */
  link?: NavItem;
  icon?: LucideIcon;
  comingSoon?: boolean;
  /**
   * Section is visible but not accessible — clicking surfaces a
   * "no access" toast. Used for modules the current user is not
   * entitled to (e.g. Reports & Analytics for non-privileged roles).
   */
  locked?: boolean;
}

/**
 * Single source of truth for sidebar navigation and page titles.
 * The router derives page titles from this config via `findNavItem`.
 */
/*
 * The Dashboard carries no permission: it is the landing screen every
 * signed-in operator is bounced to, and it aggregates only figures drawn
 * from screens they already hold a grant for.
 */
export const NAV_DASHBOARD: NavItem = {
  label: "Dashboard",
  title: "Dashboard",
  path: PATHS.dashboard,
  icon: Home,
};

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "wheel-data",
    label: "Wheelset Register",
    items: [
      {
        label: "Wheel Transactions",
        title: "Wheel Data Transactions",
        path: PATHS.wheelData.transactions,
        icon: FileText,
        permission: "wheel-register.view",
      },
    ],
  },
  {
    id: "maintenance",
    label: "Wheelset Maintenance",
    items: [
      {
        label: "Wheel Disc",
        title: "Wheel Disc Management",
        path: PATHS.maintenance.wheelDisc,
        icon: Settings,
        permission: "wheel-maintenance.view",
      },
      {
        label: "Axle",
        title: "Axle Management",
        path: PATHS.maintenance.axle,
        icon: Settings,
        permission: "wheel-maintenance.view",
      },
      {
        label: "Brake Disc",
        title: "Brake Disc Management",
        path: PATHS.maintenance.brakeDisc,
        icon: Settings,
        permission: "wheel-maintenance.view",
      },
      {
        label: "Bearing",
        title: "Bearing Management",
        path: PATHS.maintenance.bearing,
        icon: Settings,
        permission: "wheel-maintenance.view",
      },
    ],
  },
  {
    id: "assembly-dispatch",
    label: "Wheelset Assembly",
    items: [
      {
        label: "Assembly",
        title: "Wheelset Assembly",
        path: PATHS.assemblyDispatch.assembly,
        icon: Package,
        permission: "wheel-assembly.view",
      },
      {
        label: "Adjustment",
        title: "Wheelset Adjustment",
        path: PATHS.assemblyDispatch.adjustment,
        icon: Sliders,
        permission: "wheel-assembly.view",
      },
      {
        label: "Dispatch",
        title: "Wheelset Dispatch",
        path: PATHS.assemblyDispatch.dispatch,
        icon: Send,
        permission: "wheel-assembly.view",
      },
    ],
  },
  {
    /* One sidebar row, eight catalogues: the masters are administered rarely
       and belong together, so the screen carries them as tabs instead of the
       menu carrying eight near-identical rows. */
    id: "masters",
    label: "Master Management",
    items: [],
    link: {
      label: "Master Management",
      title: "Master Management",
      path: PATHS.masters.root,
      icon: Database,
      permission: "master-management.view",
      matchNested: true,
    },
  },
  {
    id: "reports",
    label: "Reports & Analytics",
    items: [],
    link: {
      label: "Reports & Analytics",
      title: "Reports & Analytics",
      path: PATHS.reports,
      icon: BarChart3,
      permission: "reports.view",
    },
  },
  {
    id: "users",
    label: "User Management",
    items: [
      {
        label: "Users",
        title: "User Management",
        path: PATHS.userManagement.userAccess,
        icon: Users,
        permission: "user-management.view",
      },
    ],
  },
];

const allItems: NavItem[] = [
  NAV_DASHBOARD,
  ...NAV_SECTIONS.flatMap((s) => (s.link ? [s.link, ...s.items] : s.items)),
];

/** Resolve the nav item for a pathname; used for header titles. */
export function findNavItem(pathname: string): NavItem | undefined {
  return (
    allItems.find((item) => item.path === pathname) ??
    /* A tabbed screen keeps its sub-page in the URL, so fall back to the
       nearest ancestor entry for the header title. */
    allItems.find((item) => item.matchNested && pathname.startsWith(item.path + "/"))
  );
}

/** An entry with no declared grant is open to every signed-in user. */
function isVisible(entry: NavItem | NavSection, has: (code: PermissionCode) => boolean): boolean {
  return entry.permission === undefined || has(entry.permission);
}

/**
 * The sections the current user may actually reach, with the items they
 * hold no grant for stripped out.
 *
 * A section whose items are all hidden is dropped entirely rather than
 * left as an empty header, so the menu never advertises a module the
 * operator cannot open. Sections that legitimately carry no items
 * (`comingSoon`, `locked`, and single-destination `link` sections) survive
 * on their own grant instead.
 */
export function visibleNavSections(has: (code: PermissionCode) => boolean): NavSection[] {
  const sections: NavSection[] = [];

  for (const section of NAV_SECTIONS) {
    if (!isVisible(section, has)) continue;

    if (section.link) {
      if (isVisible(section.link, has)) sections.push(section);
      continue;
    }

    if (section.comingSoon || section.locked) {
      sections.push(section);
      continue;
    }

    const items = section.items.filter((item) => isVisible(item, has));
    if (items.length > 0) sections.push({ ...section, items });
  }

  return sections;
}
